import { useCallback, useMemo } from 'react'
import {
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
} from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Loader2, Check } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useAuth } from 'app/frontend/base/config/context/auth'
import { useTeam } from 'app/frontend/base/config/context/team'
import Section from 'app/frontend/base/components/Section'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { useUpdateEncounterOrder } from 'app/frontend/base/config/mutations'
import { useDifficulties } from 'app/frontend/base/hooks/useDifficulties'
import Difficulty, {
  difficultiesLabels,
} from 'app/frontend/types/custom/Difficulty'
import EventActivityGrid from 'app/frontend/base/pages/TeamDashboard/subpages/TeamEvent/components/EventActivityGrid'
import { EventActivityLinkedPlanData } from 'app/frontend/base/pages/TeamDashboard/subpages/TeamEvent/hooks/useEventActivityLinkedPlanData'
import SeasonSelect from 'app/frontend/base/pages/TeamDashboard/subpages/TeamLoot/components/SeasonSelect'
import { sortEncountersForSeason } from '../encounterOrder'
import DefaultSetupProviders from './DefaultSetupProviders'
import DefaultSetupStatusSelect from './DefaultSetupStatusSelect'
import {
  buildDefaultSetupActivity,
  buildDefaultSetupGridEntries,
} from './defaultSetupEntries'
import useDefaultSetupMutations from './useDefaultSetupMutations'

const emptyLinkedPlanDataByActivityId = new Map<
  number,
  EventActivityLinkedPlanData
>()

const DefaultSetupGridPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { team } = useTeam()
  const { accessLevel } = useAuth()
  const { seasonId: seasonIdParam, difficulty: difficultyParam } = useParams()
  const {
    data: { currentExpansion, currentSeason, gameVersion },
  } = usePageInfo()
  const { difficulties: availableDifficulties } = useDifficulties()
  const plansPath = `${team.path}/events/plans`
  const seasonId = Number(seasonIdParam)
  const difficulty = availableDifficulties.includes(
    difficultyParam as Difficulty,
  )
    ? (difficultyParam as Difficulty)
    : undefined
  const {
    setups,
    isSetupsPending,
    isSaving: isSetupSaving,
    setupFor,
    upsertEnabled,
    isUpsertPending,
    updateSelection: mutateSelection,
  } = useDefaultSetupMutations(team.id, difficulty ?? availableDifficulties[0])
  const updateEncounterOrderMutation = useUpdateEncounterOrder(team)
  const isSaving = isSetupSaving || updateEncounterOrderMutation.isPending

  const selectedSeason = useMemo(() => {
    if (!Number.isFinite(seasonId)) return undefined
    return currentExpansion.seasons.find((season) => season.id === seasonId)
  }, [currentExpansion.seasons, seasonId])

  const activities = useMemo(() => {
    if (!selectedSeason || !difficulty) return []
    const instancesById = new Map(
      selectedSeason.instances.map((instance) => [instance.id, instance]),
    )
    return sortEncountersForSeason(
      selectedSeason,
      difficulty,
      team.encounterOrders,
    ).map((encounter, index) =>
      buildDefaultSetupActivity({
        encounter,
        instance: instancesById.get(encounter.instanceId),
        difficulty,
        gameVersion,
        order: index,
      }),
    )
  }, [difficulty, gameVersion, selectedSeason, team.encounterOrders])

  const entries = useMemo(
    () =>
      buildDefaultSetupGridEntries({
        team,
        members: team.members ?? [],
        activities,
        setups,
        difficulty: difficulty ?? availableDifficulties[0],
      }),
    [activities, availableDifficulties, difficulty, setups, team],
  )

  const handleUpdateSelection = useCallback(
    (
      characterId: number,
      updates: {
        role?: string
        specId?: number | null
        characterId?: number
        selected?: boolean
      },
      activityId?: number,
    ) => {
      if (activityId) {
        mutateSelection(activityId, characterId, updates)
        return
      }
      mutateSelection(
        activities.map((activity) => activity.id),
        characterId,
        updates,
      )
    },
    [activities, mutateSelection],
  )

  const handleUpdateActivityOrder = useCallback(
    async (activityOrder: number[]) => {
      if (!selectedSeason || !difficulty) return
      const encounterIds = activityOrder.map((activityId) => {
        const activity = activities.find((entry) => entry.id === activityId)
        return activity?.encounter?.id ?? activityId
      })
      await updateEncounterOrderMutation.mutateAsync({
        seasonId: selectedSeason.id,
        difficulty,
        encounterIds,
      })
    },
    [activities, difficulty, selectedSeason, updateEncounterOrderMutation],
  )

  const gridPath = (nextSeasonId: number, nextDifficulty: Difficulty) =>
    `${team.path}/events/default-setups/${nextSeasonId}/${nextDifficulty}`

  const onSeasonChange = (value?: string) => {
    if (!value || !difficulty) return
    const season = currentExpansion.seasons.find(
      (entry) => entry.id.toString() === value,
    )
    if (!season) return
    localStorage.setItem('plansSeasonId', season.id.toString())
    navigate(gridPath(season.id, difficulty), { replace: true })
  }

  const onDifficultyChange = (value: string) => {
    const next = value as Difficulty
    if (!availableDifficulties.includes(next) || !Number.isFinite(seasonId)) {
      return
    }
    localStorage.setItem('plansDifficulty', next)
    navigate(gridPath(seasonId, next), { replace: true })
  }

  if (accessLevel !== 'admin') {
    return <Navigate to={plansPath} replace />
  }

  if (!selectedSeason || !difficulty) {
    const storedSeasonId = Number(localStorage.getItem('plansSeasonId'))
    const fallbackSeason =
      currentExpansion.seasons.find((season) => season.id === storedSeasonId) ??
      currentSeason
    const storedDifficulty = localStorage.getItem(
      'plansDifficulty',
    ) as Difficulty | null
    const fallbackDifficulty =
      storedDifficulty && availableDifficulties.includes(storedDifficulty)
        ? storedDifficulty
        : availableDifficulties[0]

    if (fallbackSeason && fallbackDifficulty) {
      return (
        <Navigate
          to={gridPath(fallbackSeason.id, fallbackDifficulty)}
          replace
        />
      )
    }

    return <Navigate to={plansPath} replace />
  }

  return (
    <DefaultSetupProviders
      activities={activities}
      entries={entries}
      onUpdateSelection={handleUpdateSelection}
      onUpdateActivityOrder={handleUpdateActivityOrder}
      raidsPlanningScope='per_encounter'
    >
      <div>
        <h2 className='flex justify-between items-center gap-4 flex-wrap'>
          <div className='flex items-center gap-3 whitespace-nowrap'>
            {t('dashboard.team.plans.default-setup.page-title')}
            <Tooltip>
              <TooltipTrigger asChild>
                <span className='inline-flex'>
                  <Badge variant='outline'>
                    {isSaving ? (
                      <Loader2 className='h-3.5 w-3.5 animate-spin' />
                    ) : (
                      <Check className='h-3.5 w-3.5 text-success-foreground' />
                    )}
                  </Badge>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {isSaving ? 'Saving changes...' : 'Up to date'}
              </TooltipContent>
            </Tooltip>
          </div>
          <div className='flex items-center gap-2 flex-wrap'>
            <SeasonSelect
              className='w-52'
              value={selectedSeason.id.toString()}
              setValue={onSeasonChange}
            />
          </div>
        </h2>

        <p className='mb-6'>
          {t('dashboard.team.plans.default-setup.page-description', {
            difficulty: difficultiesLabels[difficulty],
          })}
        </p>

        <div className='flex items-end gap-2'>
          {availableDifficulties.length > 1 ? (
            <div className='flex flex-col gap-1'>
              <span className='font-semibold'>Difficulty</span>
              <Tabs value={difficulty} onValueChange={onDifficultyChange}>
                <TabsList>
                  {availableDifficulties.map((option) => (
                    <TabsTrigger key={option} value={option}>
                      {difficultiesLabels[option]}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>
          ) : null}
        </div>

        <div className='mt-2'>
          {isSetupsPending ? (
            <div className='flex justify-center py-16'>
              <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
            </div>
          ) : (
            <EventActivityGrid
              key={`${selectedSeason.id}-${difficulty}`}
              activities={activities}
              linkedPlanDataByActivityId={emptyLinkedPlanDataByActivityId}
              isRaidPlansLoading={false}
              hidePlanUi
              hideStatus
              hideEventActions
              renderActivityHeaderAction={(activity) => {
                const encounterId = activity.encounter?.id ?? activity.id
                const setup = setupFor(encounterId)
                return (
                  <DefaultSetupStatusSelect
                    enabled={setup?.enabled ?? false}
                    onEnabledChange={(nextEnabled) =>
                      upsertEnabled(encounterId, nextEnabled)
                    }
                    isPending={isUpsertPending(encounterId)}
                    className='h-8 w-full'
                  />
                )
              }}
            />
          )}
        </div>
      </div>
    </DefaultSetupProviders>
  )
}

export const DefaultSetups = () => {
  const { t } = useTranslation()
  const { team, guild } = useTeam()

  return (
    <Section
      title={`${t('dashboard.team.plans.default-setup.page-title')} | ${
        team.name
      } | ${guild.name}`}
    >
      <Routes>
        <Route path='/' element={<DefaultSetupGridPage />} />
        <Route
          path=':seasonId/:difficulty'
          element={<DefaultSetupGridPage />}
        />
      </Routes>
    </Section>
  )
}

export default DefaultSetupGridPage
